import { useQueryClient } from '@tanstack/react-query';
import { applyToolEffects } from '@/lib/mediaEffects';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useCreatePage, slugify, PageEditorMode, pagesApi, type FullPreviewData } from '@wordpuppi/shared';
import { openPreview } from '@/utils/desktopActions';
import { TiptapEditor } from '@/components/TiptapEditor';
import { TeraEditor } from '@/components/TeraEditor';
import { PageTemplateSelect } from '@/components/PageTemplateSelect';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ChevronDown } from 'lucide-react';

export const Route = createFileRoute('/sites/$siteSlug/pages/new')({
  component: NewPagePage,
});

function NewPagePage() {
  const { siteSlug } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createPage = useCreatePage(siteSlug);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [editorMode, setEditorMode] = useState<PageEditorMode>('richtext');
  const [showTitle, setShowTitle] = useState(true);
  const [showInHeader, setShowInHeader] = useState(true);
  const [showInFooter, setShowInFooter] = useState(true);
  const [customCss, setCustomCss] = useState('');
  const [customJs, setCustomJs] = useState('');
  const [template, setTemplate] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Auto-generate slug from title
  useEffect(() => {
    if (!slugManuallyEdited && title) {
      setSlug(slugify(title));
    }
  }, [title, slugManuallyEdited]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const result = await createPage.mutateAsync({
        title,
        slug,
        content,
        status,
        editor_mode: editorMode,
        show_title: showTitle,
        show_in_header: showInHeader,
        show_in_footer: showInFooter,
        custom_css: customCss || undefined,
        custom_js: customJs || undefined,
        ...(template ? { template } : {}),
      });
      void applyToolEffects(queryClient, siteSlug, { name: 'wpp_create_content', args: { site_slug: siteSlug, content_type: 'pages', data: { title, content, custom_css: customCss, custom_js: customJs } } }, { ok: true }).catch(() => {});
      toast.success('Page created');
      navigate({
        to: '/sites/$siteSlug/pages/$pageId',
        params: { siteSlug, pageId: result.data!.id },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create page');
    }
  };

  const handlePreview = async () => {
    const previewKey = `preview-${Date.now()}`;
    const previewData: FullPreviewData = {
      title: title || 'Untitled Page',
      content,
      slug: slug || 'preview',
      editor_mode: editorMode,
      custom_css: customCss || undefined,
      custom_js: customJs || undefined,
      show_title: showTitle,
      template,
    };
    const base = import.meta.env.BASE_URL.replace(/\/$/, '');
    // AB#488/#530 (browser) + AB#533 (desktop): openPreview branches on isTauri.
    try {
      await openPreview({
        browserUrl: `${base}/sites/${siteSlug}/preview?key=${previewKey}`,
        storageKey: previewKey,
        storageValue: JSON.stringify(previewData),
        render: () => pagesApi.previewFull(siteSlug, previewData),
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to open preview');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">New Page</h1>
        <Button variant="secondary" onClick={handlePreview}>
          Preview
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Page title"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="slug">Slug</Label>
          <Input
            id="slug"
            type="text"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugManuallyEdited(true);
            }}
            placeholder="page-url-slug"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            id="showTitle"
            type="checkbox"
            checked={showTitle}
            onChange={(e) => setShowTitle(e.target.checked)}
            className="h-4 w-4 rounded border-input"
          />
          <Label htmlFor="showTitle">Show title on page</Label>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="showInHeader"
            type="checkbox"
            checked={showInHeader}
            onChange={(e) => setShowInHeader(e.target.checked)}
            className="h-4 w-4 rounded border-input"
          />
          <Label htmlFor="showInHeader">Show in header navigation</Label>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="showInFooter"
            type="checkbox"
            checked={showInFooter}
            onChange={(e) => setShowInFooter(e.target.checked)}
            className="h-4 w-4 rounded border-input"
          />
          <Label htmlFor="showInFooter">Show in footer navigation</Label>
        </div>

        <PageTemplateSelect siteSlug={siteSlug} value={template} onChange={setTemplate} />

        <div className="space-y-2">
          <Label>Editor Mode</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={editorMode === 'richtext' ? 'secondary' : 'outline'}
              onClick={() => setEditorMode('richtext')}
            >
              Rich Text
            </Button>
            <Button
              type="button"
              variant={editorMode === 'tera' ? 'secondary' : 'outline'}
              onClick={() => setEditorMode('tera')}
            >
              Tera Template
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {editorMode === 'tera'
              ? 'Write a Tera template with access to site data, posts, pages, tags, and custom content types.'
              : 'Use the visual editor to create HTML content.'}
          </p>
        </div>

        <div className="space-y-2">
          <Label>Content</Label>
          {editorMode === 'tera' ? (
            <TeraEditor content={content} onChange={setContent} siteSlug={siteSlug} />
          ) : (
            <TiptapEditor content={content} onChange={setContent} siteSlug={siteSlug} folder="pages" />
          )}
        </div>

        {/* Advanced: Custom CSS/JS */}
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setAdvancedOpen(!advancedOpen)}
            className="flex items-center gap-2 font-medium text-sm hover:text-foreground/80"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ease-out ${advancedOpen ? 'rotate-180' : ''}`} />
            Advanced Options
          </button>
          {advancedOpen && (
            <div className="disclosure-reveal space-y-4 pl-6 border-l-2 border-muted">
              <div className="space-y-2">
                <Label htmlFor="customCss">Custom CSS</Label>
                <Textarea
                  id="customCss"
                  value={customCss}
                  onChange={(e) => setCustomCss(e.target.value)}
                  placeholder="/* CSS styles for this page only */"
                  className="font-mono text-sm min-h-[100px]"
                />
                <p className="text-xs text-muted-foreground">
                  CSS will be injected into the page's &lt;head&gt; tag.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customJs">Custom JavaScript</Label>
                <Textarea
                  id="customJs"
                  value={customJs}
                  onChange={(e) => setCustomJs(e.target.value)}
                  placeholder="// JavaScript for this page only"
                  className="font-mono text-sm min-h-[100px]"
                />
                <p className="text-xs text-muted-foreground">
                  JavaScript will be injected before the closing &lt;/body&gt; tag.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select value={status} onValueChange={(v: string) => setStatus(v as 'draft' | 'published')}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2 pt-4">
          <Button type="submit" disabled={createPage.isPending}>
            {createPage.isPending ? 'Creating...' : 'Create Page'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate({ to: '/sites/$siteSlug/pages', params: { siteSlug } })}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
